import { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ContextMenuComponent from '../../../../../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../../../../../components/stories/organisms/context-menu/constants'
import TableEditButton from '../../../../../../../components/stories/organisms/table/components/table-edit-button'
import { useSnackbar } from '../../../../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../../../../components/stories/molecules/snackbar/constants'
import { useUpdateProjectsBatchMutation } from '../../../../../../../react-query/project/mutation/use-update-projects-batch-mutation'
import { projectTreeItemTypeOptions } from '../../../../../../../react-query/project/query/use-get-project-tree-query'
import { useDeleteFolderMutation } from '../../../../../../../react-query/project/mutation/use-delete-folder'
import { ContextMenuTitle } from '../../../../../styled-component'
import CompanyMoveMultipleItemsModal from '../../../../../components/move-items-modals/company-move-multiple-items-modal'
import DeactivateProjectAlert from '../../../../deactivate-project-alert'
import DeleteFolderAlert from '../../../../../../../components/delete-folder-alert'

const canUsePermission = (permissions, permission) =>
  permissions?.includes(permission)

const getMoveItemsMessage = ({ foldersCount, projectsCount }) => {
  if (foldersCount && !projectsCount) {
    return { id: 'projects_moveFolders' }
  }

  if (!foldersCount && projectsCount) {
    return { id: 'projects_moveProjects' }
  }

  return { id: 'projects_moveSelectedItems' }
}

const CompanyProjectsTableEditMenu = ({
  formatMessage,
  onBatchComplete = () => {},
  selectedItems = [],
  userPermissions = []
}) => {
  const history = useHistory()
  const { addSnackbar } = useSnackbar()
  const [isOpen, setIsOpen] = useState(false)
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)
  const [isSetInactivePopupOpen, setIsSetInactivePopupOpen] = useState(false)
  const [isDeleteFolderAlertOpen, setIsDeleteFolderAlertOpen] = useState(false)

  const selectedProjects = useMemo(
    () =>
      selectedItems.filter(
        (item) => item.type === projectTreeItemTypeOptions.PROJECT
      ),
    [selectedItems]
  )

  const selectedFolders = useMemo(
    () =>
      selectedItems.filter(
        (item) => item.type === projectTreeItemTypeOptions.FOLDER
      ),
    [selectedItems]
  )

  const seletedFolderToDeleteIds = useMemo(
    () =>
      selectedFolders
        .filter((folder) => folder.items === 0)
        .map((folder) => folder.id),
    [selectedFolders]
  )

  const selectedProjectActivatedIds = useMemo(
    () =>
      selectedProjects
        .filter((project) => project.active)
        .map((project) => project.id),
    [selectedProjects]
  )

  const selectedProjectInactivatedIds = useMemo(
    () =>
      selectedProjects
        .filter((project) => !project.active)
        .map((project) => project.id),
    [selectedProjects]
  )

  const hasInactiveProjects = selectedProjectInactivatedIds.length > 0
  const hasActiveProjects = selectedProjectActivatedIds.length > 0
  const hasSelectedItems = selectedItems.length > 0
  const hasSelectedProjects = selectedProjects.length > 0
  const canCreateFolders = canUsePermission(
    userPermissions,
    'CreateProjectFolders'
  )
  const canCreateProjects = canUsePermission(userPermissions, 'CreateProjects')

  const handleUpdateProjectsError = useCallback(() => {
    addSnackbar({
      message: formatMessage({ id: 'error' }),
      snackbarType: snackbarVariantOptions.ERROR,
      hideCloseButton: true
    })
  }, [addSnackbar, formatMessage])

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage(
        { id: 'folder_deleted' },
        { count: selectedProjectActivatedIds.length }
      ),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  const showSnackbarProjectToggleIsActiveSuccess = (data) => {
    const { active } = data[0]

    const updatedProjectsCount = data.length ?? 1

    addSnackbar({
      message: formatMessage(
        {
          id: active ? 'project_reactivated' : 'project_deactivated'
        },
        { count: updatedProjectsCount }
      ),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // mutates
  const handleProjectsUpdated = useCallback(
    (data) => {
      showSnackbarProjectToggleIsActiveSuccess(data)
      onBatchComplete()
    },
    [addSnackbar, formatMessage, onBatchComplete]
  )

  const { mutate: updateProjectsBatch, isPending: isUpdatingProjectsBatch } =
    useUpdateProjectsBatchMutation({
      onSuccess: handleProjectsUpdated,
      onError: handleUpdateProjectsError
    })

  const { mutate: deleteFolder, isPending: deleteFolderLoading } =
    useDeleteFolderMutation({
      onSuccess: () => {
        setIsDeleteFolderAlertOpen(false)
        showSnackbarSuccess()
      }
    })

  const handleDeleteFolder = () => {
    deleteFolder({ folderIdList: seletedFolderToDeleteIds })
  }

  // handlers
  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const handleNewFolder = useCallback(() => {
    history.push('/project/newfolder')
  }, [history])

  const handleMoveItems = useCallback(() => {
    setIsMoveModalOpen(true)
  }, [])

  const handleSetProjectsInactive = useCallback(() => {
    if (!hasSelectedProjects) return

    setIsSetInactivePopupOpen(true)
  }, [hasSelectedProjects])

  const handleConfirmSetProjectsInactive = useCallback(() => {
    updateProjectsBatch({
      projectIds: selectedProjectActivatedIds,
      body: { active: false }
    })
  }, [selectedProjects, updateProjectsBatch])

  const handleConfirmSetProjectsActive = useCallback(() => {
    updateProjectsBatch({
      projectIds: selectedProjectInactivatedIds,
      body: { active: true }
    })
  }, [selectedProjects, updateProjectsBatch])

  const handleItemClick = (item) => (event) => {
    event.stopPropagation()

    if (item.disabled) return

    setIsOpen(false)
    item.action()
  }

  const menuItems = useMemo(
    () =>
      [
        {
          id: 'move-selected-items',
          title: formatMessage(
            getMoveItemsMessage({
              foldersCount: selectedFolders.length,
              projectsCount: selectedProjects.length
            })
          ),
          action: handleMoveItems,
          hide: !canCreateFolders,
          disabled: !hasSelectedItems,
          showDivider: true
        },
        {
          id: 'delete-folder',
          title: formatMessage(
            { id: 'deleteFolder' },
            {
              count: seletedFolderToDeleteIds.length
            }
          ),
          action: () => setIsDeleteFolderAlertOpen(true),
          disabled: seletedFolderToDeleteIds.length === 0,
          hide: !canCreateFolders,
          variant: contextMenuVariantOptions.DESTRUCTIVE,
          showDivider: true
        },
        {
          id: 'activate_projects',
          title: formatMessage(
            { id: 'activate_projects' },
            { count: selectedProjectInactivatedIds.length }
          ),
          action: handleConfirmSetProjectsActive,
          hide: !canCreateProjects || !hasInactiveProjects,
          disabled: !hasInactiveProjects,
          variant: hasInactiveProjects
            ? contextMenuVariantOptions.STANDARD
            : contextMenuVariantOptions.RESTRICTED,
          showDivider: false
        },
        {
          id: 'deactivate_projects',
          title: formatMessage(
            { id: 'deactivate_projects' },
            {
              count: selectedProjectActivatedIds.length
            }
          ),
          action: handleSetProjectsInactive,
          hide: !canCreateProjects,
          disabled: !hasActiveProjects,
          variant: hasActiveProjects
            ? contextMenuVariantOptions.DESTRUCTIVE
            : contextMenuVariantOptions.RESTRICTED,
          showDivider: false
        }
      ].filter(({ hide }) => !hide),
    [
      canCreateFolders,
      canCreateProjects,
      formatMessage,
      handleMoveItems,
      handleNewFolder,
      handleSetProjectsInactive,
      handleConfirmSetProjectsActive,
      hasSelectedItems,
      hasSelectedProjects,
      selectedFolders.length,
      selectedProjects.length
    ]
  )

  const isDisabled =
    !hasSelectedItems || !menuItems.length || isUpdatingProjectsBatch

  if (isDisabled) {
    return <TableEditButton disabled />
  }

  return (
    <>
      <ContextMenuComponent.Root isOpen={isOpen} setIsOpen={setIsOpen}>
        <ContextMenuComponent.Trigger callback={handleTriggerClick}>
          <TableEditButton disabled={false} />
        </ContextMenuComponent.Trigger>
        <ContextMenuComponent.List
          width="220px"
          align={contextMenuAlignOptions.START}
          side={contextMenuSideOptions.BOTTOM}
          arrow
          arrowAlignment={contextMenuArrowAlignmentOptions.END}
        >
          <ContextMenuTitle>
            {formatMessage(
              { id: 'x_item_selected' },
              { item: selectedItems.length }
            )}
          </ContextMenuTitle>
          {menuItems.map((item) => (
            <>
              <ContextMenuComponent.Item
                key={item.id}
                id={item.id}
                variant={item.variant}
                padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                titleWeight={contextMenuTitleWeightOptions['TEXT-600']}
                titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                onClick={handleItemClick(item)}
                title={item.title}
                disabled={item.disabled}
              />
              {item.showDivider && <ContextMenuComponent.Divider />}
            </>
          ))}
        </ContextMenuComponent.List>
      </ContextMenuComponent.Root>

      {isMoveModalOpen && (
        <CompanyMoveMultipleItemsModal
          onClose={() => setIsMoveModalOpen(false)}
          onMoved={onBatchComplete}
          selectedFolders={selectedFolders}
          selectedProjects={selectedProjects}
        />
      )}

      {isSetInactivePopupOpen && (
        <DeactivateProjectAlert
          onClose={() => setIsSetInactivePopupOpen(false)}
          onConfirm={handleConfirmSetProjectsInactive}
          isLoading={isUpdatingProjectsBatch}
        />
      )}

      <DeleteFolderAlert
        isOpen={isDeleteFolderAlertOpen}
        onClose={() => setIsDeleteFolderAlertOpen(false)}
        onDelete={handleDeleteFolder}
        onDeleteLoading={deleteFolderLoading}
        description={formatMessage({
          id: 'projectMenu_delete_bulk_folder_description'
        })}
        title={formatMessage(
          { id: 'deleteFolder' },
          { count: seletedFolderToDeleteIds.length }
        )}
      />
    </>
  )
}

export default CompanyProjectsTableEditMenu
